/**
 * Configurator gallery open/closed layer visibility (production sec_new-configurator parity).
 * Works with ee-configurator-add-ons (is-active) and ee-configurator-swatch (variant overlay).
 *
 * Loaded only by ee-new-configurator — safe for other product templates.
 */
(() => {
  const OPEN_SCROLL_THRESHOLD = 200;
  const MOBILE_GALLERY_MAX = 749;
  let lastOpenMode = null;
  let lastScrollState = null;
  let updatingVisibility = false;
  let stackSyncScheduled = false;

  function isOpenScrollMode() {
    return window.scrollY > OPEN_SCROLL_THRESHOLD;
  }

  function isMobileGallery() {
    return window.innerWidth <= MOBILE_GALLERY_MAX;
  }

  function getGalleryViewers() {
    return document.querySelectorAll('slider-component[id^="GalleryViewer-"]');
  }

  /**
   * iOS Safari traps z-index inside overflow scrollers. Keep add-on layers as siblings
   * of .variant-overlay (not inside the slider ul) so crown/tote/drawer can paint above it.
   */
  function ensureAddonLayerStack(viewer) {
    if (!viewer) return null;

    let stack = null;
    let overlay = null;
    Array.from(viewer.children).forEach((child) => {
      if (!stack && child.classList?.contains('configurator-addon-layer-stack')) stack = child;
      if (!overlay && child.classList?.contains('variant-overlay')) overlay = child;
    });
    if (stack) return stack;

    stack = document.createElement('div');
    stack.className = 'configurator-addon-layer-stack';
    stack.setAttribute('data-configurator-addon-stack', 'true');
    stack.setAttribute('aria-hidden', 'true');

    if (overlay) {
      overlay.after(stack);
    } else {
      viewer.appendChild(stack);
    }
    return stack;
  }

  function promoteAddonLayers(viewer) {
    if (!viewer) return;
    const stack = ensureAddonLayerStack(viewer);
    if (!stack) return;

    const layers = viewer.querySelectorAll('.product__media-list > [data-add-on-media-id]');
    layers.forEach((layer) => {
      if (layer.parentElement === stack) return;
      stack.appendChild(layer);
    });
  }

  function promoteAllAddonLayers() {
    getGalleryViewers().forEach(promoteAddonLayers);
  }

  function ensureAddonImagesLoaded(layer) {
    if (!layer) return;
    layer.querySelectorAll('img').forEach((img) => {
      try {
        if (img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
          img.loading = 'eager';
        }
        // Nudge iOS to fetch images that started life visibility:hidden.
        if (img.complete && img.naturalWidth > 0) return;
        const src = img.currentSrc || img.getAttribute('src');
        if (src) {
          img.setAttribute('src', src);
        }
        if (typeof img.decode === 'function') {
          img.decode().catch(() => {});
        }
      } catch (_) {}
    });
  }

  function syncHasActiveAddons(viewer) {
    if (!viewer) return;
    const anyShown = Array.from(viewer.querySelectorAll('[data-add-on-media-id].is-active')).some(
      (el) => el.style.visibility === 'visible' || el.classList.contains('opacity-100')
    );
    viewer.classList.toggle('has-active-addons', anyShown);
  }

  function clearMobileStackVars(viewer) {
    [
      '--configurator-stack-top',
      '--configurator-stack-left',
      '--configurator-stack-width',
      '--configurator-stack-height',
    ].forEach((prop) => viewer.style.removeProperty(prop));
  }

  function syncMobileGalleryStack() {
    stackSyncScheduled = false;
    getGalleryViewers().forEach((viewer) => {
      promoteAddonLayers(viewer);

      if (!isMobileGallery()) {
        clearMobileStackVars(viewer);
        return;
      }

      const activeSlide =
        viewer.querySelector('.product__media-item.is-active:not([data-add-on-media-id])') ||
        viewer.querySelector('.product__media-list > .product__media-item:not([data-add-on-media-id])');
      const overlay = viewer.querySelector('.variant-overlay.visible');
      const viewerRect = viewer.getBoundingClientRect();

      let anchorRect = null;
      if (activeSlide) {
        const anchor = activeSlide.querySelector('.product-media-container') || activeSlide;
        anchorRect = anchor.getBoundingClientRect();
      }
      // iOS can report 0x0 for visibility:hidden slides under swatch-overlay-active.
      if ((!anchorRect || anchorRect.width < 1 || anchorRect.height < 1) && overlay) {
        anchorRect = overlay.getBoundingClientRect();
      }
      if (!anchorRect || anchorRect.width < 1 || anchorRect.height < 1) {
        anchorRect = viewerRect;
      }

      const top = anchorRect.top - viewerRect.top;
      const left = anchorRect.left - viewerRect.left;

      viewer.style.setProperty('--configurator-stack-top', `${Math.max(0, top)}px`);
      viewer.style.setProperty('--configurator-stack-left', `${Math.max(0, left)}px`);
      viewer.style.setProperty('--configurator-stack-width', `${anchorRect.width}px`);
      viewer.style.setProperty('--configurator-stack-height', `${anchorRect.height}px`);
    });
  }

  function requestMobileStackSync() {
    if (stackSyncScheduled) return;
    stackSyncScheduled = true;
    requestAnimationFrame(syncMobileGalleryStack);
  }

  function updateImageVisibility(openMode) {
    if (updatingVisibility) return;
    updatingVisibility = true;

    try {
      promoteAllAddonLayers();
      const effectiveAdd = !!openMode;

      document.dispatchEvent(
        new CustomEvent('configurator:layer-mode-change', { detail: { openMode: effectiveAdd } })
      );

      document.querySelectorAll('[id^="GalleryViewer"] [data-open-image]').forEach((image) => {
        const isAddOnLayer = image.hasAttribute('data-add-on-media-id');

        if (image.dataset.openImage === 'true') {
          if (effectiveAdd) {
            image.classList.add('peer-[.is-active]:opacity-100');
          } else {
            image.classList.remove('peer-[.is-active]:opacity-100');
          }
          image.classList.toggle('add-on-open-image', effectiveAdd);

          if (!isAddOnLayer) return;

          const shouldShow = effectiveAdd && image.classList.contains('is-active');
          image.classList.toggle('opacity-100', shouldShow);
          image.classList.toggle('opacity-0', !shouldShow);
          image.style.opacity = shouldShow ? '1' : '0';
          image.style.visibility = shouldShow ? 'visible' : 'hidden';
          // Let section CSS control stacking (crown/tote/drawer). Inline z-index was
          // flattening closed layers to the same level as .variant-overlay on iOS.
          image.style.zIndex = '';
          if (shouldShow) ensureAddonImagesLoaded(image);
        } else {
          if (effectiveAdd) {
            image.classList.remove('peer-[.is-active]:opacity-100');
          } else {
            image.classList.add('peer-[.is-active]:opacity-100');
          }
          image.classList.remove('add-on-open-image');

          if (!isAddOnLayer) return;

          const shouldShow = !effectiveAdd && image.classList.contains('is-active');
          image.classList.toggle('opacity-100', shouldShow);
          image.classList.toggle('opacity-0', !shouldShow);
          image.style.opacity = shouldShow ? '1' : '0';
          image.style.visibility = shouldShow ? 'visible' : 'hidden';
          image.style.zIndex = '';
          if (shouldShow) ensureAddonImagesLoaded(image);
        }
      });

      getGalleryViewers().forEach(syncHasActiveAddons);
    } finally {
      updatingVisibility = false;
      requestMobileStackSync();
    }
  }

  function applyScrollState() {
    const isScrollAbove200 = window.scrollY > OPEN_SCROLL_THRESHOLD;
    const isScrollAtTop = window.scrollY < 1;
    const nextState = isScrollAbove200 ? 'above' : isScrollAtTop ? 'top' : lastScrollState;
    if (!nextState || nextState === lastScrollState) return;

    lastScrollState = nextState;
    updateImageVisibility(nextState === 'above');
  }

  function onScrollThresholdCross() {
    const openMode = isOpenScrollMode();
    if (openMode === lastOpenMode) return;
    lastOpenMode = openMode;
    updateImageVisibility(openMode);
  }

  function bindGalleryVisibility() {
    if (window.EeConfiguratorGallery?.bound) return;

    lastOpenMode = isOpenScrollMode();
    lastScrollState = lastOpenMode ? 'above' : 'top';

    document.querySelectorAll('.js-image-update').forEach((el) => {
      el.addEventListener('click', () => updateImageVisibility(isOpenScrollMode()));
    });

    document.addEventListener('change', (event) => {
      const t = event.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.closest('ee-variant-selects') && t.matches('input[type="radio"]')) {
        updateImageVisibility(isOpenScrollMode());
        return;
      }
      if (t.closest('configurator-add-ons') && t.matches('input[type="checkbox"], input[type="radio"]')) {
        const addOnsEl = document.querySelector('configurator-add-ons');
        if (addOnsEl?.syncAllSelectedLayers) addOnsEl.syncAllSelectedLayers(isOpenScrollMode());
        updateImageVisibility(isOpenScrollMode());
      }
    });

    document.addEventListener('configurator:addon-layer-change', () => {
      const addOnsEl = document.querySelector('configurator-add-ons');
      if (addOnsEl?.syncAllSelectedLayers) {
        addOnsEl.syncAllSelectedLayers(isOpenScrollMode());
      }
      updateImageVisibility(isOpenScrollMode());
    });

    window.addEventListener('scroll', onScrollThresholdCross, { passive: true });
    window.addEventListener('scroll', applyScrollState, { passive: true });
    window.addEventListener('scroll', requestMobileStackSync, { passive: true });
    window.addEventListener('resize', requestMobileStackSync, { passive: true });
    window.addEventListener('orientationchange', requestMobileStackSync, { passive: true });

    document.querySelectorAll('[id^="Slider-Gallery-"]').forEach((slider) => {
      slider.addEventListener('scroll', requestMobileStackSync, { passive: true });
    });

    getGalleryViewers().forEach((viewer) => {
      promoteAddonLayers(viewer);
      viewer.addEventListener('slideChanged', requestMobileStackSync);
    });

    document.addEventListener('configurator:addon-layer-change', requestMobileStackSync);

    promoteAllAddonLayers();
    updateImageVisibility(isOpenScrollMode());
    applyScrollState();
    requestMobileStackSync();

    window.EeConfiguratorGallery = {
      OPEN_SCROLL_THRESHOLD,
      MOBILE_GALLERY_MAX,
      isOpenScrollMode,
      updateImageVisibility,
      requestMobileStackSync,
      promoteAddonLayers: promoteAllAddonLayers,
      bound: true,
    };
  }

  window.EeConfiguratorGallery = window.EeConfiguratorGallery || {
    OPEN_SCROLL_THRESHOLD,
    MOBILE_GALLERY_MAX,
    isOpenScrollMode,
    updateImageVisibility,
    requestMobileStackSync,
    promoteAddonLayers: promoteAllAddonLayers,
    bound: false,
  };

  // Defer scripts run after DOM parse — promote immediately so slider init
  // does not treat add-on layers as swipeable slides.
  promoteAllAddonLayers();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindGalleryVisibility);
  } else {
    bindGalleryVisibility();
  }
})();
